/**
 * "Become a partner" — the Xero-style guided onboarding (owner spec, 3 Jul 2026).
 * Steps: about you → licensing (adviser = own AFSL; accountant = operates under AgedCareWise's
 * arrangements) → create login (+ email-confirm resume via a localStorage draft) → trial/payment
 * note → create firm (pending Jon's approval) → portal.
 */
import React, { useEffect, useState } from 'react';
import { X, Building2, ShieldCheck, UserPlus, CheckCircle2, AlertCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import { signIn, completeTwoFactorChallenge, signUp, signOut, getSessionUser, setUserName, verifySignupCode, resendSignupCode } from '../../services/authClient.ts';
import TwoFactorSetup from '../../components/auth/TwoFactorSetup.tsx';
import type { SessionUser } from '../../services/authClient.ts';
import { registerPartner } from '../../services/tenantApi.ts';
import { acceptTerms } from '../../services/termsApi.ts';
import { PARTNER_TERMS, PRIVACY_POLICY } from '../../constants/legal/index.ts';
import CollectionNotice from '../../components/legal/CollectionNotice.tsx';
import LegalDocModal from '../../components/legal/LegalDocModal.tsx';

const DRAFT_KEY = 'awisePartnerWizardDraft';

import { PROFESSION_LABELS, type Profession } from '../../services/tenantApi.ts';
import { confirmAction } from '../../components/ui/notices.tsx';

/**
 * The one distinction the sign-up form actually turns on. A licensed adviser firm gives its own
 * advice under its own AFSL and is asked about it on the next step. Every other firm — accountant,
 * solicitor, provider, trustee — refers its clients to AgedCareWise, which gives the advice under
 * its own licence; they are asked to acknowledge that, and nothing about licensing. Which of the
 * "other" professions the firm is goes on its record and changes nothing else.
 */
type FirmKind = 'adviser' | 'other';

/** The "other" professions, in the order they are offered. 'adviser' has its own card. */
const OTHER_PROFESSIONS = (['accountant', 'solicitor', 'aged_care_provider', 'support_at_home_provider', 'super_fund', 'other'] as const)
  .map((key) => ({ key, label: PROFESSION_LABELS[key] === 'Other professional' ? 'Something else' : PROFESSION_LABELS[key] }));

/** Shared shape of PARTNER_TERMS / PRIVACY_POLICY (src/constants/legal/*.ts). */
interface LegalDoc {
  key: string;
  version: string;
  title: string;
  effectiveDate: string;
  draft: boolean;
  body: string;
}

interface WizardDraft {
  step?: number;
  firmName?: string;
  firstName?: string;
  lastName?: string;
  profession?: Profession | null;
  afslLicenseeName?: string;
  afslNumber?: string;
  adviceStructure?: AdviceStructure | null;
  carName?: string;
  carNumber?: string;
  adviserNumber?: string;
  acknowledged?: boolean;
  awaitingAuth?: boolean;
}

interface Notice {
  tone: 'error' | 'success';
  text: string;
}

/** registerPartner's success branch — see services/tenantApi.ts. */
interface RegisterPartnerResult {
  ok: true;
  tenantId: string;
  status: string;
}

export function getPartnerWizardDraft(): WizardDraft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearPartnerWizardDraft() {
  localStorage.removeItem(DRAFT_KEY);
}

const inputClass =
  'w-full rounded-xl border border-gray-200 bg-gray-50/70 px-3.5 py-2.5 text-gray-900 placeholder-gray-400 shadow-sm transition focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-500/40 focus:outline-none';
const primaryBtn =
  'inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#1a6fd4] to-[#17b8e0] px-6 py-2.5 font-semibold text-white shadow-lg shadow-blue-600/25 transition hover:shadow-xl hover:brightness-[1.04] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none';
const backBtn = 'text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1';

const STEPS = ['About you', 'Licensing', 'Your login', 'Finish'];
/** The second step asks an adviser firm about its licence, and tells every other firm who gives the advice. */
const STEPS_NO_LICENCE = ['About you', 'Who gives the advice', 'Your login', 'Finish'];

/** Matches Tenant['adviceStructure'] — kept local so the wizard needs no extra import. */
type AdviceStructure = 'own_afsl' | 'corporate_ar' | 'individual_ar';

/*
 * How the advice reaches the client, which is NOT the same question as "are you licensed?".
 *
 * For an advice firm the second question is always yes and tells you nothing. The three answers
 * below differ in which entities the advice document must name — one, two or three of them — and a
 * firm that has not answered cannot produce a compliant Statement of Advice.
 */
const ADVICE_STRUCTURES: { value: AdviceStructure; title: string; detail: string }[] = [
  { value: 'own_afsl', title: 'Under our own AFSL', detail: 'The practice is the licensee.' },
  {
    value: 'corporate_ar',
    title: 'Through our practice, as a corporate authorised representative',
    detail: 'The practice is authorised by a licensee.',
  },
  {
    value: 'individual_ar',
    title: 'By me personally, as an authorised representative',
    detail: 'Authorised by a licensee, with no practice company.',
  },
];

interface PartnerOnboardingWizardProps {
  onClose: () => void;
  onComplete: (result: RegisterPartnerResult) => void;
}

const PartnerOnboardingWizard = ({ onClose, onComplete }: PartnerOnboardingWizardProps) => {
  const draft = getPartnerWizardDraft() || {};
  const [step, setStep] = useState(draft.step || 0);
  const [firmName, setFirmName] = useState(draft.firmName || '');
  const [firstName, setFirstName] = useState(draft.firstName || '');
  const [lastName, setLastName] = useState(draft.lastName || '');
  const [profession, setProfession] = useState<Profession | null>(draft.profession || null);
  // Derived from a saved draft so a returning person sees the right card lit.
  const [firmKind, setFirmKind] = useState<FirmKind | null>(draft.profession ? (draft.profession === 'adviser' ? 'adviser' : 'other') : null);
  const stepTitles = firmKind === 'other' ? STEPS_NO_LICENCE : STEPS;
  const [afslLicenseeName, setAfslLicenseeName] = useState(draft.afslLicenseeName || '');
  const [afslNumber, setAfslNumber] = useState(draft.afslNumber || '');
  const [adviceStructure, setAdviceStructure] = useState<AdviceStructure | null>(draft.adviceStructure || null);
  const [carName, setCarName] = useState(draft.carName || '');
  const [carNumber, setCarNumber] = useState(draft.carNumber || '');
  const [adviserNumber, setAdviserNumber] = useState(draft.adviserNumber || '');
  const [acknowledged, setAcknowledged] = useState(draft.acknowledged || false);
  const [agreedPartnerTerms, setAgreedPartnerTerms] = useState(false); // firm-level clickwrap, ticked at the Finish step
  const [viewDoc, setViewDoc] = useState<LegalDoc | null>(null); // legal document open in the reader
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [haveAccount, setHaveAccount] = useState(false);
  // Single-screen email confirmation: after sign-up we show a code box right here (no link, no
  // second tab), mirroring the client flow.
  const [awaitingCode, setAwaitingCode] = useState(false);
  /** The second sign-in step: held only between the password and the code, and never a session. */
  const [awaitingMfa, setAwaitingMfa] = useState(false);
  /*
   * A brand-new firm owner has to set up multi-factor authentication before they reach the portal. Not a
   * nicety: the moment their firm exists they are an adviser, and the client files, firm routes and
   * SOAs all refuse a password-only session — so "Open your portal" would land them somewhere that
   * says no to everything, with nothing on screen explaining why.
   */
  const [settingUpMfa, setSettingUpMfa] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [code, setCode] = useState('');
  const [signedInAs, setSignedInAs] = useState<SessionUser | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [result, setResult] = useState<RegisterPartnerResult | null>(null);

  // Keep a draft so wizard progress survives a page reload. (Email confirmation is now a
  // single-screen code — no second tab — so the legacy awaitingAuth resume path is dormant.)
  useEffect(() => {
    // Preserve the awaitingAuth marker until sign-in completes — overwriting it made the
    // email-confirmation tab treat a new PARTNER as a plain client (owner-reported bug).
    const prior = getPartnerWizardDraft() || {};
    localStorage.setItem(
      DRAFT_KEY,
      JSON.stringify({
        step, firmName, firstName, lastName, profession, afslLicenseeName, afslNumber, acknowledged,
        adviceStructure, carName, carNumber, adviserNumber,
        ...(prior.awaitingAuth && !signedInAs ? { awaitingAuth: true } : {}),
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, firmName, firstName, lastName, profession, afslLicenseeName, afslNumber, acknowledged, signedInAs,
      adviceStructure, carName, carNumber, adviserNumber]);

  useEffect(() => {
    getSessionUser().then((user) => {
      setSignedInAs(user);
      // Skip the login step ONLY when genuinely resuming the email-confirmation round trip
      // (awaitingAuth draft). A leftover session from a previous visit must NOT be silently
      // adopted as the firm owner — the login step stays visible with a 'use a different email'
      // escape hatch (owner-reported: it grabbed a stale email with no way to change it).
      if (user && step === 2 && getPartnerWizardDraft()?.awaitingAuth) setStep(3);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Escape hatch: sign out of the leftover/wrong account and enter a different one. */
  const useDifferentAccount = async () => {
    setBusy(true);
    setNotice(null);
    try {
      await signOut().catch(() => {});
      setSignedInAs(null);
      setEmail('');
      setPassword('');
      setHaveAccount(false);
    } finally {
      setBusy(false);
    }
  };

  const noticeCard = notice && (
    <div className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white shadow-sm px-4 py-3">
      <span
        className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
          notice.tone === 'error' ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'
        }`}
      >
        {notice.tone === 'error' ? <AlertCircle size={16} /> : <CheckCircle2 size={16} />}
      </span>
      <p className="text-sm text-gray-700 leading-relaxed">{notice.text}</p>
    </div>
  );

  const next = () => {
    setNotice(null);
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => {
    setNotice(null);
    setStep((s) => Math.max(s - 1, 0));
  };

  /** The authenticator step. The same box takes a recovery code, for someone without their phone. */
  const handleMfaStep = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setNotice(null);
    try {
      const res = await completeTwoFactorChallenge(mfaToken, code.trim(), true);
      if (!res.ok) {
        setCode('');
        return setNotice({ tone: 'error', text: res.message });
      }
      setAwaitingMfa(false);
      setMfaToken('');
      setCode('');
      setSignedInAs(await getSessionUser());
      next();
    } finally {
      setBusy(false);
    }
  };

  const handleLoginStep = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setNotice(null);
    try {
      if (signedInAs) {
        next();
        return;
      }
      if (haveAccount) {
        const res = await signIn(email.trim(), password);
        if (!res.ok) {
          /*
           * Their password was right and their account already has an authenticator on it. Advisers
           * are required to, so this is the ordinary case for anyone returning here — and without
           * this branch they would be told to confirm an email they confirmed long ago.
           */
          if ('mfaRequired' in res && res.mfaRequired) {
            setMfaToken(res.mfaToken);
            setCode('');
            setAwaitingMfa(true);
            return setNotice({
              tone: 'success',
              text: 'Enter the 6-digit code from your authenticator app to finish signing in.',
            });
          }
          return setNotice({ tone: 'error', text: 'message' in res ? res.message : 'Please confirm your email before signing in.' });
        }
      } else {
        if (password.length < 10) {
          return setNotice({ tone: 'error', text: 'Please use a password of at least 10 characters.' });
        }
        const res = await signUp(email.trim(), password);
        if (!res.ok) return setNotice({ tone: 'error', text: res.message });
        if (res.needsEmailConfirm) {
          // Stay right here and ask for the emailed code — no confirmation link, no second tab.
          setCode('');
          setAwaitingCode(true);
          return setNotice({
            tone: 'success',
            text: `We've emailed a code to ${email.trim()}. Enter it below to confirm your email and continue — check your junk folder if it's not there within a minute.`,
          });
        }
      }
      setSignedInAs(await getSessionUser());
      next();
    } catch (err) {
      /*
       * The API could not be reached at all.
       *
       * signUp/signIn use fetch, which THROWS on a refused or failed connection rather than
       * returning a result — so without this branch the whole handler unwound silently and the
       * button appeared to do nothing at all. A person then retypes their password, tries a
       * different email, and concludes the product is broken, which is very nearly the right
       * conclusion and tells them nothing useful. Every other failure here already explains itself;
       * this was the one that did not.
       */
      setNotice({
        tone: 'error',
        text:
          "We couldn't reach AgedCareWise just now. Check your connection and try again — nothing you've " +
          'typed has been lost.',
      });
      // eslint-disable-next-line no-console
      console.error('partner sign-up: the API call failed', err);
    } finally {
      setBusy(false);
    }
  };

  /** Closing mid-wizard destroys the typed details — never do that silently. */
  const requestClose = async () => {
    if (!result && (step > 0 || firmName.trim() || profession)) {
      const sure = await confirmAction('Leave partner setup? The details you’ve entered here will be cleared.');
      if (!sure) return;
    }
    onClose();
  };

  /** Confirm the emailed code (single screen), then move on to the trial step. */
  const handleVerifyCode = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const res = await verifySignupCode(email.trim(), code);
      if (!res.ok) {
        return setNotice({ tone: 'error', text: "That code wasn't accepted — check the digits in your email and try again." });
      }
      setAwaitingCode(false);
      setCode('');
      setSignedInAs(await getSessionUser());
      next();
    } finally {
      setBusy(false);
    }
  };

  const handleResendCode = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const res = await resendSignupCode(email.trim());
      if (!res.ok) return setNotice({ tone: 'error', text: res.message || 'Could not resend the code — please try again.' });
      setNotice({ tone: 'success', text: `A fresh code is on its way to ${email.trim()}.` });
    } finally {
      setBusy(false);
    }
  };

  const handleCreateFirm = async () => {
    setBusy(true);
    setNotice(null);
    try {
      // Bind the owner's name to their LOGIN — advice attribution derives from the account.
      await setUserName(firstName, lastName).catch(() => {});
      const res = await registerPartner({
        name: firmName.trim(),
        // Guaranteed non-null here — the Continue button past step 0 is disabled (stepValid) until
        // profession is chosen.
        profession: profession as Profession,
        afslLicenseeName: afslLicenseeName.trim(),
        afslNumber: afslNumber.trim(),
        adviceStructure,
        carName: carName.trim(),
        carNumber: carNumber.trim(),
        adviserNumber: adviserNumber.trim(),
      });
      if (!res.ok) {
        // Each login can own only one firm — point the way out instead of a raw error.
        const friendly = /already own/i.test(res.message || '')
          ? `The account you're signed in as${signedInAs?.email ? ` (${signedInAs.email})` : ''} already runs a firm on AgedCareWise. Go Back to the login step and choose "Use a different email" to create another firm.`
          : res.message;
        return setNotice({ tone: 'error', text: friendly });
      }
      clearPartnerWizardDraft();
      // Record the firm's clickwrap acceptance on the server ledger (session is live here).
      // Fire-and-forget: a transient failure must not block onboarding.
      acceptTerms(['partner_terms', 'privacy_policy'], 'partner_onboarding').catch(() => {});
      setResult(res);
    } finally {
      setBusy(false);
    }
  };

  const stepValid =
    (step === 0 && firmName.trim() && firstName.trim() && lastName.trim() && profession) ||
    // An adviser firm must say HOW its advice is provided, not merely that it is licensed: the
    // answer decides which entities an SOA names, and there is no safe default to guess.
    (step === 1 &&
      (profession === 'adviser' ? Boolean(adviceStructure) && Boolean(afslLicenseeName.trim()) : acknowledged)) ||
    step >= 2;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
      <div className="w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-black/5 max-h-[92vh] overflow-y-auto">
        {/* Gradient hero */}
        <div className="relative overflow-hidden bg-gradient-to-br from-[#1a6fd4] to-[#17b8e0] px-6 pt-6 pb-6 text-white">
          <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
          <button type="button" onClick={requestClose}
            className="absolute right-4 top-4 rounded-full p-1.5 text-white/80 transition hover:bg-white/15 hover:text-white" aria-label="Close">
            <X size={18} />
          </button>
          <div className="flex items-center gap-3.5">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/25 backdrop-blur">
              <Building2 className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold leading-tight">Become a partner</h2>
              <p className="text-sm text-white/85">
                {result ? 'All done' : `Step ${step + 1} of ${STEPS.length} — ${stepTitles[step]}`}
              </p>
            </div>
          </div>
          {/* Progress bar in the hero */}
          {!result && (
            <div className="mt-4 flex gap-1.5">
              {stepTitles.map((s, i) => (
                <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? 'bg-white' : 'bg-white/25'}`} />
              ))}
            </div>
          )}
        </div>

        <div className="p-6 space-y-4">
          {noticeCard}

          {result && settingUpMfa ? (
            <TwoFactorSetup
              mandatory
              onDone={() => {
                setSettingUpMfa(false);
                onComplete(result);
              }}
            />
          ) : result ? (
            <div className="space-y-4 text-center py-4">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                <CheckCircle2 size={30} />
              </span>
              <h3 className="text-lg font-bold text-gray-900">{firmName} is live</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                One last thing before you go in. Adviser accounts are protected by a code from your
                phone as well as a password — it takes about a minute, and you only do it once.
              </p>
              <button type="button" onClick={() => setSettingUpMfa(true)} className={`${primaryBtn} mx-auto`}>
                Set up multi-factor authentication <ArrowRight size={16} />
              </button>
            </div>
          ) : step === 0 ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="pw-first-name" className="block text-sm font-medium text-gray-700 mb-1">First name</label>
                  <input id="pw-first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} className={inputClass} placeholder="e.g. Sam" />
                </div>
                <div>
                  <label htmlFor="pw-last-name" className="block text-sm font-medium text-gray-700 mb-1">Surname</label>
                  <input id="pw-last-name" value={lastName} onChange={(e) => setLastName(e.target.value)} className={inputClass} placeholder="e.g. Taylor" />
                </div>
              </div>
              <div>
                <label htmlFor="pw-firm" className="block text-sm font-medium text-gray-700 mb-1">Firm name</label>
                <input id="pw-firm" value={firmName} onChange={(e) => setFirmName(e.target.value)} className={inputClass} placeholder="e.g. Taylor Advisory Pty Ltd" />
              </div>
              <div>
                <p className="block text-sm font-medium text-gray-700 mb-2">Which best describes your firm?</p>
                <div className="grid grid-cols-2 gap-3">
                  {([
                    { key: 'adviser', label: 'Licensed financial adviser', hint: 'Your firm gives the advice under its own AFSL' },
                    { key: 'other', label: 'Other professional', hint: 'AgedCareWise gives the advice — your firm refers clients' },
                  ] satisfies { key: FirmKind; label: string; hint: string }[]).map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => {
                        // Picking "other" without saying which leaves the firm unnamed, and the step
                        // gate below holds until the dropdown is answered. Switching back to adviser
                        // drops whatever was chosen there — it would be a lie on an adviser's record.
                        setProfession(opt.key === 'adviser' ? 'adviser' : profession === 'adviser' || !profession ? null : profession);
                        setFirmKind(opt.key);
                      }}
                      className={`text-left rounded-xl border-2 p-3 transition-colors ${
                        firmKind === opt.key ? 'border-blue-600 bg-blue-50/60' : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <p className="text-sm font-semibold text-gray-900">{opt.label}</p>
                      <p className="text-xs text-gray-500 mt-1 leading-snug">{opt.hint}</p>
                    </button>
                  ))}
                </div>
                {firmKind === 'other' && (
                  <div className="mt-3">
                    <label htmlFor="wizard-profession" className="block text-sm font-medium text-gray-700 mb-1">Which profession?</label>
                    <select
                      id="wizard-profession"
                      value={profession ?? ''}
                      onChange={(e) => setProfession((e.target.value || null) as Profession | null)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="">Choose one…</option>
                      {OTHER_PROFESSIONS.map((opt) => (
                        <option key={opt.key} value={opt.key}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          ) : step === 1 ? (
            <div className="space-y-4">
              {profession === 'adviser' ? (
                <>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Every Statement of Advice has to name the entities behind it. Which ones depends on how
                    your advice reaches the client.
                  </p>

                  <div className="space-y-2">
                    {ADVICE_STRUCTURES.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setAdviceStructure(opt.value)}
                        className={`w-full text-left rounded-xl border px-4 py-3 transition ${
                          adviceStructure === opt.value
                            ? 'border-[#1a6fd4] border-2 bg-blue-50/60'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <span className="block text-sm font-semibold text-gray-900">{opt.title}</span>
                        <span className="block text-xs text-gray-500 mt-0.5">{opt.detail}</span>
                      </button>
                    ))}
                  </div>

                  {adviceStructure && (
                    <div className="rounded-xl border-l-2 border-[#1a6fd4] bg-blue-50/40 px-4 py-3 space-y-4">
                      {adviceStructure === 'corporate_ar' && (
                        <div className="space-y-3">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-[#1a6fd4]">The practice</p>
                          <div>
                            <label htmlFor="pw-car-name" className="block text-sm font-medium text-gray-700 mb-1">Practice (CAR) name</label>
                            <input id="pw-car-name" value={carName} onChange={(e) => setCarName(e.target.value)} className={inputClass} placeholder="e.g. Taylor Advice Pty Ltd" />
                          </div>
                          <div>
                            <label htmlFor="pw-car-number" className="block text-sm font-medium text-gray-700 mb-1">CAR number</label>
                            <input id="pw-car-number" value={carNumber} onChange={(e) => setCarNumber(e.target.value)} className={inputClass} placeholder="e.g. 234567" />
                          </div>
                        </div>
                      )}

                      <div className="space-y-3">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#1a6fd4]">
                          {adviceStructure === 'own_afsl' ? 'Your licence' : 'The licensee'}
                        </p>
                        <div>
                          <label htmlFor="pw-licensee" className="block text-sm font-medium text-gray-700 mb-1">
                            {adviceStructure === 'own_afsl' ? 'Licensee name' : 'Licensee'}
                          </label>
                          <input id="pw-licensee" value={afslLicenseeName} onChange={(e) => setAfslLicenseeName(e.target.value)} className={inputClass} placeholder="e.g. Taylor Wealth Licensing Pty Ltd" />
                        </div>
                        <div>
                          <label htmlFor="pw-afsl" className="block text-sm font-medium text-gray-700 mb-1">AFSL number</label>
                          <input id="pw-afsl" value={afslNumber} onChange={(e) => setAfslNumber(e.target.value)} className={inputClass} placeholder="e.g. 345678" />
                        </div>
                      </div>

                      {/* The person's OWN number, which is a different entity's from the CAR number
                          above: that one belongs to the practice company, this one to them. Asked here
                          only because the person signing up is themselves an adviser — everyone
                          invited later gives their own, and it follows them between practices. */}
                      <div className="space-y-3">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#1a6fd4]">You</p>
                        <div>
                          <label htmlFor="pw-adviser-no" className="block text-sm font-medium text-gray-700 mb-1">
                            Your ASIC adviser number
                          </label>
                          <input id="pw-adviser-no" value={adviserNumber} onChange={(e) => setAdviserNumber(e.target.value)} className={inputClass} placeholder="e.g. 123456" />
                          <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                            {adviceStructure === 'own_afsl'
                              ? 'Your number on the Financial Advisers Register. As an employee or director of your own licensee you are a representative of it, not an authorised representative, and your advice will not describe you as one.'
                              : 'Your number on the Financial Advisers Register — the same number as your authorised representative appointment.'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white shadow-sm px-4 py-3">
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                      <ShieldCheck size={16} />
                    </span>
                    <div className="text-sm text-gray-700 leading-relaxed space-y-2">
                      <p>
                        Your firm doesn&apos;t need a financial-services licence to use AgedCareWise, because
                        your firm isn&apos;t the one giving the advice. <strong>AgedCareWise gives the advice,
                        under its own AFSL</strong>, and every Statement of Advice says so.
                      </p>
                      <p>
                        Your firm&apos;s role is to refer clients and help them through the process. Your firm
                        is not an authorised representative of AgedCareWise, and must not present itself to
                        clients as the provider of the advice.
                      </p>
                    </div>
                  </div>
                  <label className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer">
                    <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    I understand that AgedCareWise gives the advice under its own licence, and that my firm
                    refers clients and does not give financial advice through AgedCareWise.
                  </label>
                </>
              )}
            </div>
          ) : step === 2 ? (
            signedInAs ? (
              <div className="space-y-3">
                <div className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white shadow-sm px-4 py-3">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                    <CheckCircle2 size={16} />
                  </span>
                  <p className="text-sm text-gray-700">You&apos;re signed in as <strong>{signedInAs.email}</strong> — this will be the firm owner&apos;s login.</p>
                </div>
                <button type="button" onClick={useDifferentAccount} disabled={busy}
                  className="text-sm text-blue-600 hover:underline">
                  Not you? Use a different email
                </button>
              </div>
            ) : awaitingMfa ? (
              <form onSubmit={handleMfaStep} className="space-y-4">
                <p className="text-sm text-gray-600">
                  Your password was right. Now enter the 6-digit code from your authenticator app.
                </p>
                <input type="text" inputMode="text" autoComplete="one-time-code" maxLength={20} value={code} autoFocus
                  onChange={(e) => setCode(e.target.value)}
                  className={`${inputClass} text-center text-2xl font-mono tracking-[0.35em]`}
                  placeholder="000000" aria-label="Authentication code" />
                <p className="text-xs text-gray-500">
                  Lost your phone? Enter one of your recovery codes here instead.
                </p>
                <button type="button" disabled={busy}
                  onClick={() => { setAwaitingMfa(false); setMfaToken(''); setCode(''); setNotice(null); }}
                  className="text-sm text-blue-600 hover:underline">
                  Back
                </button>
              </form>
            ) : awaitingCode ? (
              <form onSubmit={(e) => { e.preventDefault(); handleVerifyCode(); }} className="space-y-4">
                <p className="text-sm text-gray-600">
                  Enter the code we emailed to <strong>{email}</strong>. It can take a minute to
                  arrive — please check your junk or spam folder before asking for a new one.
                </p>
                <input type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={10} value={code} autoFocus
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className={`${inputClass} text-center text-2xl font-mono tracking-[0.35em]`} placeholder="Enter code" />
                <button type="button" onClick={handleResendCode} disabled={busy}
                  className="text-sm text-gray-500 hover:text-gray-700">
                  Didn&apos;t get it? Resend the code
                </button>
              </form>
            ) : (
              <form onSubmit={handleLoginStep} className="space-y-4">
                <p className="text-sm text-gray-600">This login becomes the firm owner&apos;s account.</p>
                <div>
                  <label htmlFor="pw-email" className="block text-sm font-medium text-gray-700 mb-1">Work email</label>
                  <input id="pw-email" type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} placeholder="you@yourfirm.com.au" />
                </div>
                <div>
                  <label htmlFor="pw-password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input id="pw-password" type="password" required autoComplete={haveAccount ? 'current-password' : 'new-password'} value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} placeholder="At least 10 characters" />
                </div>
                <button type="button" onClick={() => setHaveAccount(!haveAccount)} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                  <UserPlus size={14} /> {haveAccount ? 'New here? Create an account instead' : 'Already have an AgedCareWise login? Sign in instead'}
                </button>
              </form>
            )
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Quick check before we create your firm:</p>
              <div className="rounded-xl border border-gray-200 divide-y divide-gray-100 text-sm">
                <div className="px-4 py-2.5 flex justify-between gap-4"><span className="text-gray-500">Firm</span><span className="font-medium text-gray-900 text-right">{firmName}</span></div>
                <div className="px-4 py-2.5 flex justify-between gap-4"><span className="text-gray-500">Owner</span><span className="font-medium text-gray-900 text-right">{`${firstName} ${lastName}`.trim()}{signedInAs ? ` (${signedInAs.email})` : ''}</span></div>
                <div className="px-4 py-2.5 flex justify-between gap-4"><span className="text-gray-500">Type</span><span className="font-medium text-gray-900 text-right">{profession === 'adviser' ? 'Licensed financial adviser' : profession ? PROFESSION_LABELS[profession] : '—'}</span></div>
                {/*
                  One row per ENTITY, each named.
                  A corporate authorised representative has two companies standing behind the advice —
                  the licensee and the practice — and a single "Licence" line running them together
                  reads as one organisation with an odd name. The person signing has to be able to
                  check each of them, because each is named separately on the advice their clients
                  will read.
                */}
                {profession === 'adviser' ? (
                  <>
                    <div className="px-4 py-2.5 flex justify-between gap-4"><span className="text-gray-500">Licensee</span><span className="font-medium text-gray-900 text-right">{afslLicenseeName}{afslNumber ? ` · AFSL ${afslNumber}` : ''}</span></div>
                    {adviceStructure === 'corporate_ar' && (carName || carNumber) && (
                      <div className="px-4 py-2.5 flex justify-between gap-4"><span className="text-gray-500">Practice (CAR)</span><span className="font-medium text-gray-900 text-right">{carName}{carNumber ? ` · CAR ${carNumber}` : ''}</span></div>
                    )}
                    {adviserNumber.trim() && (
                      <div className="px-4 py-2.5 flex justify-between gap-4"><span className="text-gray-500">Your adviser number</span><span className="font-medium text-gray-900 text-right">{adviserNumber.trim()}</span></div>
                    )}
                  </>
                ) : (
                  <div className="px-4 py-2.5 flex justify-between gap-4"><span className="text-gray-500">Advice given by</span><span className="font-medium text-gray-900 text-right">AgedCareWise, under its AFSL</span></div>
                )}
              </div>
              <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-gray-200 bg-gray-50/70 px-3.5 py-3">
                <input
                  type="checkbox"
                  checked={agreedPartnerTerms}
                  onChange={(e) => setAgreedPartnerTerms(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-xs leading-relaxed text-gray-600">
                  {/*
                    "Authorised to bind" is precise and standard, and it is also the line a financial
                    adviser stopped and asked about — which is the wrong place to lose someone, on the
                    last screen before they commit their firm. The substance is unchanged and still
                    carries both warranties: that this person may agree FOR the company, and that they
                    are agreeing on its behalf. Only the jargon is gone.

                    This is the acceptance UI, not the agreement: the clickwrap ledger records which
                    DOCUMENT VERSION was accepted (partnerTerms.ts's `version`), so rewording the
                    label here changes nothing about what anyone agreed to and needs no version bump.
                  */}
                  I have authority to agree to these terms for {firmName.trim() || 'this firm'}, and on its behalf I have read
                  and agree to the{' '}
                  <button type="button" onClick={() => setViewDoc(PARTNER_TERMS)}
                    className="font-semibold text-blue-600 underline decoration-blue-300 underline-offset-2 hover:text-blue-700">
                    Partner Terms
                  </button>{' '}
                  and the{' '}
                  <button type="button" onClick={() => setViewDoc(PRIVACY_POLICY)}
                    className="font-semibold text-blue-600 underline decoration-blue-300 underline-offset-2 hover:text-blue-700">
                    Privacy Policy
                  </button>
                  .
                </span>
              </label>
              {/* APP 5: the firm's own staff details are personal information too. */}
              <CollectionNotice variant="partner" className="mt-3" />
            </div>
          )}

          {!result && (
            <div className="flex items-center justify-between pt-2">
              {step > 0 ? (
                <button
                  type="button"
                  onClick={
                    awaitingCode
                      ? () => { setAwaitingCode(false); setNotice(null); }
                      : back
                  }
                  disabled={busy}
                  className={backBtn}
                >
                  <ArrowLeft size={14} /> Back
                </button>
              ) : (
                <span />
              )}
              {step === 2 ? (
                awaitingCode ? (
                  <button type="button" onClick={handleVerifyCode} disabled={busy || code.length < 6} className={primaryBtn}>
                    {busy ? 'Checking…' : 'Confirm & continue'} <ArrowRight size={16} />
                  </button>
                ) : (
                  <button type="button" onClick={handleLoginStep} disabled={busy || (!signedInAs && (!email || !password))} className={primaryBtn}>
                    {busy ? 'Working…' : 'Continue'} <ArrowRight size={16} />
                  </button>
                )
              ) : step === STEPS.length - 1 ? (
                <button type="button" onClick={handleCreateFirm} disabled={busy || !agreedPartnerTerms} className={primaryBtn}>
                  {busy ? 'Creating…' : 'Create my firm'} <ArrowRight size={16} />
                </button>
              ) : (
                <button type="button" onClick={next} disabled={!stepValid || busy} className={primaryBtn}>
                  Continue <ArrowRight size={16} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {viewDoc && <LegalDocModal doc={viewDoc} onClose={() => setViewDoc(null)} />}
    </div>
  );
};

export default PartnerOnboardingWizard;
